import type { TokenResponse, JwtClaims, User } from "@/types/api";

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN as string;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID as string;
const REDIRECT_URI = import.meta.env.VITE_COGNITO_REDIRECT_URI as string;

// Bound Cognito token calls so a stale persisted session cannot leave /app on
// the route-level spinner forever when the auth endpoint is blocked or stalls.
const TOKEN_REQUEST_TIMEOUT_MS = 10_000;

// PKCE storage key (sessionStorage survives redirect, cleared on tab close)
const PKCE_VERIFIER_KEY = "prayingmantis_pkce_verifier";

/** Generate a cryptographically random code verifier for PKCE (RFC 7636). */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/** Compute SHA-256 code challenge from verifier. */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

/** Base64url encode (no padding) per RFC 7636. */
function base64UrlEncode(bytes: Uint8Array): string {
  const binString = Array.from(bytes, (b) => String.fromCharCode(b)).join("");
  return btoa(binString).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Build the Cognito Hosted UI authorization URL with PKCE. */
export async function getLoginUrl(): Promise<string> {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);

  // Store verifier for token exchange after redirect
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: "openid email profile",
    code_challenge: challenge,
    code_challenge_method: "S256",
  });
  return `${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
}

/** Build the Cognito logout URL. */
export function getLogoutUrl(): string {
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: `${window.location.origin}/login`,
  });
  return `${COGNITO_DOMAIN}/logout?${params.toString()}`;
}

async function postTokenRequest(body: URLSearchParams): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), TOKEN_REQUEST_TIMEOUT_MS);

  try {
    return await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeout);
  }
}

/** Exchange an authorization code for tokens (with PKCE verifier). */
export async function exchangeCodeForTokens(code: string): Promise<TokenResponse> {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    code,
  });

  if (verifier) {
    body.set("code_verifier", verifier);
  }

  const res = await postTokenRequest(body);

  if (!res.ok) {
    throw new Error(`Token exchange failed: ${res.status}`);
  }

  const tokens = await res.json();
  if (!tokens.access_token || !tokens.id_token) {
    throw new Error("Token exchange returned incomplete response");
  }

  return tokens;
}

/** Refresh an access token using a refresh token. */
export async function refreshTokens(refreshToken: string): Promise<TokenResponse> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: CLIENT_ID,
    refresh_token: refreshToken,
  });

  const res = await postTokenRequest(body);

  if (!res.ok) {
    throw new Error(`Token refresh failed: ${res.status}`);
  }

  const tokens = await res.json();
  if (!tokens.access_token || !tokens.id_token) {
    throw new Error("Token refresh returned incomplete response");
  }

  return tokens;
}

/** Decode a JWT payload without verification (verification happens server-side). */
export function decodeJwt(token: string): JwtClaims {
  const payload = token.split(".")[1];
  const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(decoded);
}

/**
 * Roles the UI gates nav on. The platform writes `custom:role` / `custom:is_msp`
 * (see src/api/routes/msp.py, src/onboarding/provisioner.py) and never assigns
 * Cognito groups, so groups alone left every user role-less. Groups are still
 * unioned in for users grouped by hand.
 */
function extractRoles(claims: JwtClaims): string[] {
  const roles = new Set(claims["cognito:groups"] ?? []);
  if (claims["custom:role"]) roles.add(claims["custom:role"].toLowerCase());
  if (claims["custom:is_msp"]?.toLowerCase() === "true") roles.add("msp");
  return [...roles];
}

/** Extract user info from an ID token. */
export function extractUser(idToken: string): User {
  const claims = decodeJwt(idToken);
  return {
    sub: claims.sub,
    email: claims.email ?? "",
    username: claims["cognito:username"] ?? claims.email ?? claims.sub,
    tenantId: claims["custom:tenant_id"] ?? "",
    roles: extractRoles(claims),
    isSuperadmin: claims["custom:is_superadmin"]?.toLowerCase() === "true",
  };
}

/** Check if a token is expired (with 60s buffer). */
export function isTokenExpired(token: string): boolean {
  try {
    const claims = decodeJwt(token);
    return Date.now() / 1000 > claims.exp - 60;
  } catch {
    return true;
  }
}
